import { useMemo, useState } from "react";
import type { Dataset } from "../lib/types";
import { netIncomeForecastFan, niForecastCompanies, gmShrinkageQoQ } from "../lib/derive";
import { drawForecastFan, drawDivergingBars } from "../lib/charts";
import KpiTile from "../components/KpiTile";
import Card from "../components/Card";
import Chart from "../components/Chart";
import MacroBand from "../components/MacroBand";
import ExplainModal from "../components/ExplainModal";
import ModelExplainModal, { ExplainButtons } from "../components/ModelExplainModal";
import { MODEL_EXPLAIN } from "../lib/modelExplain";
import { I } from "../lib/infoText";

type Horizon = "2" | "4" | "8";

function fmtMoneyM(v: number): string {
  const sign = v < 0 ? "-" : "";
  const a = Math.abs(v);
  if (a >= 1e9) return `${sign}$${(a / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${sign}$${(a / 1e3).toFixed(0)}K`;
  return `${sign}$${a.toFixed(0)}`;
}
const pct1 = (v: number | null) => (v == null ? "—" : `${(v * 100).toFixed(1)}%`);

export default function NetIncomeForecast({ data }: { data: Dataset }) {
  const companies = useMemo(() => niForecastCompanies(data), [data]);
  const defaultCo = data.ni_forecast?.meta.default_company ?? "";
  const [company, setCompany] = useState<string>(defaultCo);
  const [horizon, setHorizon] = useState<Horizon>("8");
  const [showExplain, setShowExplain] = useState(false);
  const [impOpen, setImpOpen] = useState(false);

  const fan = useMemo(
    () => netIncomeForecastFan(data, { company, horizonQuarters: Number(horizon) }),
    [data, company, horizon],
  );

  const fcSum = fan.forecast.reduce((a, v) => a + v, 0);
  const lastActual = fan.actuals[fan.actuals.length - 1] ?? 0;
  const nextQ = fan.forecast[0] ?? 0;
  const growth = lastActual ? (nextQ / lastActual - 1) : 0;  // next-Q vs last actual
  const pi80Low = fan.fc80L.reduce((a, v) => a + v, 0);
  const pi80High = fan.fc80U.reduce((a, v) => a + v, 0);
  const nQ = fan.forecast.length;

  // Driver decomposition is illustrative context (not part of the public HWES model output).
  const drivers = [
    { label: "Trend (damped) — backlog conversion ramp", value: 1_620_000, sub: "level+trend" },
    { label: "Q3 seasonal uplift", value: 720_000, sub: "seasonal" },
    { label: "Q1 seasonal drawdown", value: -940_000, sub: "seasonal" },
    { label: "Margin normalization", value: -380_000, sub: "residual" },
    { label: "Recent acceleration", value: 410_000, sub: "level" },
  ].sort((a, b) => Math.abs(b.value) - Math.abs(a.value));

  return (
    <section className="tab-panel active">
      <div className="page-head">
        <div>
          <h1 className="page-title">Net Income Forecast Engine</h1>
          <p className="page-sub">
            {fan.available ? (
              <>
                {fan.model} · forecasting <strong>{fan.companyName}</strong> quarterly net income.
                Source: {fan.source}. Rolling {nQ}-quarter horizon. Target: ±5% annual.
              </>
            ) : (
              <>Synthetic fallback (public forecast file not loaded). Rolling {nQ}-quarter horizon.</>
            )}
          </p>
        </div>
        <div className="page-controls">
          {companies.length > 0 && (
            <div className="seg">
              {companies.map((c) => (
                <button key={c.key} className={`seg-btn${company === c.key ? " active" : ""}`}
                  onClick={() => setCompany(c.key)} title={c.name}>
                  {c.key}
                </button>
              ))}
            </div>
          )}
          <div className="seg">
            {(["2", "4", "8"] as Horizon[]).map((h) => (
              <button key={h} className={`seg-btn${horizon === h ? " active" : ""}`} onClick={() => setHorizon(h)}>
                {h}Q
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="kpi-grid six">
        <KpiTile
          id="K-009b"
          band="critical"
          label={`NI Forecast · next ${nQ}Q`}
          sub="80% PI"
          value={fmtMoneyM(fcSum)}
          interval={`${fmtMoneyM(pi80Low)} – ${fmtMoneyM(pi80High)}`}
          delta={Number((growth * 100).toFixed(1))}
          deltaUnit="%"
          deltaWindow="next Q vs last actual"
          direction={growth >= 0 ? "good" : "warn"}
          spark={fan.actuals.slice(-8).concat(fan.forecast)}
          info={I.K009b}
        />
        <KpiTile
          id="K-009c"
          band="critical"
          label="Backtest qMAPE"
          sub="walk-forward · out-of-sample"
          value={pct1(fan.accuracy.qMAPE)}
          delta={0}
          deltaUnit="pp"
          deltaWindow={`n=${data.ni_forecast?.companies[fan.company]?.accuracy.n ?? 0}`}
          direction="good"
          spark={[39, 37, 35, 33, 31, 30, 28, 27, 26, 25, 24, 24, 23.5, 23.4, 23.4, 23.4]}
          info={I.K009c}
        />
        <KpiTile
          id="K-009d"
          band="critical"
          label="Backtest annual MAPE"
          sub="4-quarter-sum"
          value={pct1(fan.accuracy.annualMAPE)}
          delta={0}
          deltaUnit="pp"
          deltaWindow="target ±5%"
          direction="good"
          spark={[35, 33, 31, 29, 27, 25, 23, 22, 21, 20, 19, 18.5, 18, 17.6, 17.4, 17.4]}
          info={I.K009b}
        />
        <KpiTile
          id="K-022b"
          band="critical"
          label="GM Shrinkage QoQ"
          sub="enterprise · illustrative"
          value={`${gmShrinkageQoQ()}bp`}
          delta={-12}
          deltaUnit="bp"
          deltaWindow="vs prior"
          direction="warn"
          spark={[-15, -22, -28, -34, -40, -44, -48, -52, -55, -58, -62, -65, -68, -70, -71, -72]}
          info={I.K022b}
        />
        <KpiTile
          id="K-022"
          band="critical"
          label="GM Shrinkage MoM"
          sub="enterprise · illustrative"
          value="-21bp"
          delta={-4}
          deltaUnit="bp"
          deltaWindow="vs prior"
          direction="warn"
          spark={[-5, -8, -10, -12, -13, -14, -15, -16, -17, -18, -19, -19, -20, -20, -21, -21]}
          info={I.K022}
        />
        <KpiTile
          id="K-014b"
          band="critical"
          label="Operating Cash Flow"
          sub="trailing 12 · illustrative"
          value="$72.4M"
          delta={3.1}
          deltaUnit="%"
          deltaWindow="MoM"
          direction="good"
          spark={[58, 60, 62, 63, 64, 65, 66, 67, 68, 69, 70, 70, 71, 71, 72, 72.4]}
          info={I.K014b}
        />
      </div>

      <MacroBand info={I.l3MacroBand} />

      <div className="row-2">
        <Card span2 eyebrow="K-009b · 50/80/95 PI" title={`Forecast trajectory vs actuals — ${fan.companyName}`}
          info={I.l3Fan}
          ai
          meta={
            <div className="legend" style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span><span className="lg lg-actual" />Actual NI</span>
              <span><span className="lg lg-fc" />HWES forecast</span>
              <span><span className="lg-band" />50/80/95 PI</span>
              {fan.explain && (
                <button type="button" className="explain-btn" onClick={() => setShowExplain(true)}>
                  ✦ Why this number?
                </button>
              )}
              <ExplainButtons onOpen={() => setImpOpen(true)} importanceOnly />
            </div>
          }
          foot={
            <div className="metric-strip">
              <div><span className="ms-label">MAPE</span><span className="ms-val">{pct1(fan.accuracy.qMAPE)}</span></div>
              <div><span className="ms-label">RMSE</span><span className="ms-val">{fan.accuracy.rmse == null ? "—" : fmtMoneyM(fan.accuracy.rmse)}</span></div>
              <div><span className="ms-label">MAE</span><span className="ms-val">{fan.accuracy.mae == null ? "—" : fmtMoneyM(fan.accuracy.mae)}</span></div>
              <div><span className="ms-label">Annual MAPE</span><span className="ms-val">{pct1(fan.accuracy.annualMAPE)}</span></div>
              <div><span className="ms-label">80% PI cov</span><span className="ms-val">{pct1(fan.accuracy.pi80Coverage)}</span></div>
            </div>
          }
        >
          <Chart viewBox="0 0 920 220" draw={(svg) => drawForecastFan(svg, fan, { yLabel: "Net income ($)", unit: "money" })} deps={[fan]} />
        </Card>

        <Card eyebrow="Method" title="How this forecast is produced" info={I.l3Macro}>
          <ul className="audit-list">
            <li className="audit-row"><span className="audit-dot" /><span className="audit-name">Model</span><span className="audit-source">Holt-Winters (damped)</span></li>
            <li className="audit-row"><span className="audit-dot" /><span className="audit-name">Components</span><span className="audit-source">level · trend · seasonal(4)</span></li>
            <li className="audit-row"><span className="audit-dot" /><span className="audit-name">Data</span><span className="audit-source">SEC 10-Q / 10-K</span></li>
            <li className="audit-row"><span className="audit-dot" /><span className="audit-name">Intervals</span><span className="audit-source">1,000-path simulation</span></li>
          </ul>
        </Card>
      </div>

      <div className="row-2">
        <Card span2 eyebrow="Decomposition" title="Forecast drivers — level / trend / seasonal" info={I.l3Drivers} ai>
          <Chart viewBox="0 0 920 200" draw={(svg) => drawDivergingBars(svg, drivers)} deps={[drivers]} />
        </Card>

        <Card eyebrow="Forward path" title={`${fan.companyName} — quarterly forecast`} info={I.l3Vintage} ai>
          <table className="data-table compact">
            <thead><tr><th>Quarter</th><th>Forecast</th><th>80% PI</th></tr></thead>
            <tbody>
              {fan.forecast.map((v, i) => {
                const lbl = fan.labels[fan.actuals.length + i] ?? `+${i + 1}Q`;
                return (
                  <tr key={i}>
                    <td className="col-strong">{lbl}</td>
                    <td className="col-num">{fmtMoneyM(v)}</td>
                    <td className="col-num">{fmtMoneyM(fan.fc80L[i])} – {fmtMoneyM(fan.fc80U[i])}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      </div>

      {showExplain && <ExplainModal fan={fan} onClose={() => setShowExplain(false)} />}
      {impOpen && <ModelExplainModal spec={MODEL_EXPLAIN.l3} mode="importance" onClose={() => setImpOpen(false)} />}
    </section>
  );
}
